import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate, useParams} from "react-router-dom";
import {Calendar} from "primereact/calendar";
import {Tag} from "primereact/tag";
import {ProgressSpinner} from "primereact/progressspinner";

import appStore from "../../../../storage/AppStore";
import clubStore from "../../../../storage/ClubStore";
import BillingQuery from "../../../../models/BillingQuery";
import Select from "../../../../ui/Select";
import Btn from "../../../../ui/Btn";
import PageLayout from "../../../PageLayout";
import NumericPagination from "../../../NumericPagination";

import "../../../../common/billing.css";

const formatDate = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const displayDate = (value) => {
    if (!value) return "";
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
};

const MemberBillings = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const billing = clubStore.billing;
    const memberBillings = billing.memberBillings;
    const memberId = Number(id);
    const companyId = clubStore.club.id;

    const [filter] = useState(() => {
        const query = new BillingQuery();
        query.setProperty("date_from", null);
        query.setProperty("date_to", null);
        return query;
    });
    const [appliedFilters, setAppliedFilters] = useState({});

    const paymentStatuses = [
        {id: true, name: appStore.tr.paid},
        {id: false, name: appStore.tr.unpaid}
    ];
    const approvalStatuses = [
        {id: true, name: appStore.tr.approved},
        {id: false, name: appStore.tr.not_approved}
    ];
    const paymentMethods = [
        {id: "cash", name: appStore.tr.cash},
        {id: "transfer", name: appStore.tr.transfer}
    ];

    useEffect(() => {
        if (!companyId || !memberId) return;
        if (clubStore.billing_types.length === 0) clubStore.getBillingTypes();
        billing.getMemberBillings({
            company_id: companyId,
            company_user_id: memberId,
            page_number: 1,
            page_size: 30
        });
    }, [companyId, memberId]);

    const loadBillings = (page = 1, filters = appliedFilters) => {
        if (!companyId || !memberId) return;
        return billing.getMemberBillings({
            company_id: companyId,
            company_user_id: memberId,
            page_number: page,
            page_size: 30,
            ...filters
        });
    };

    const search = () => {
        if (filter.date_from && filter.date_to && filter.date_from > filter.date_to) return;

        const filters = {
            date_from: formatDate(filter.date_from),
            date_to: formatDate(filter.date_to),
            billing_type_id: filter.billing_type_id || 0,
            payment_method: filter.payment_method || "",
            is_paid: filter.is_paid,
            is_approved: filter.is_approved
        };
        setAppliedFilters(filters);
        loadBillings(1, filters);
    };

    const resetFilters = () => {
        filter.setProperty("date_from", null);
        filter.setProperty("date_to", null);
        filter.setProperty("billing_type_id", 0);
        filter.setProperty("payment_method", "");
        filter.setProperty("is_paid", null);
        filter.setProperty("is_approved", null);
        setAppliedFilters({});
        loadBillings(1, {});
    };

    const BillItem = ({bill}) => {
        const group = bill.billing_group || {};
        const paid = Boolean(bill.is_paid);
        const approved = Boolean(bill.is_approved);

        return (
            <div className="billing-card">
                <div className="billing-top">
                    <div className="billing-main">
                        <div className="billing-title">
                            {group.name || bill.description || bill.billing_type?.name}
                        </div>
                        <div className="billing-sub mt-1">{bill.billing_type?.name}</div>
                        {(group.period_from || group.period_to) && (
                            <div className="text-600 text-sm mt-2">
                                <i className="pi pi-calendar mr-2"/>
                                {displayDate(group.period_from)}
                                {group.period_to && group.period_to !== group.period_from &&
                                    ` — ${displayDate(group.period_to)}`}
                            </div>
                        )}
                        {bill.notes && <div className="billing-notes mt-2">{bill.notes}</div>}
                    </div>
                    <div className="billing-side">
                        <div className="billing-amount">
                            {bill.amount} {bill.currency?.sign || ""}
                        </div>
                        <div className="billing-status">
                            <Tag severity={!paid ? "danger" : approved ? "success" : "warning"}
                                 value={paid ? appStore.tr.paid : appStore.tr.unpaid}/>
                            {paid && !approved && (
                                <Tag severity="warning" className="ml-1" value={appStore.tr.not_approved}/>
                            )}
                            {paid && bill.payment_method && (
                                <Tag severity="info" className="ml-1"
                                     value={bill.payment_method === "cash" ? appStore.tr.cash : appStore.tr.transfer}/>
                            )}
                        </div>
                    </div>
                </div>
                <div className="billing-actions">
                    <Btn icon="pi pi-eye" severity="success" outlined label={appStore.tr.view}
                         onClick={() => navigate(`/manager/billing/detail/${bill.id}`)}/>
                </div>
            </div>
        );
    };

    const title = memberBillings.company_user?.id === memberId
        ? memberBillings.company_user.title : "";

    return (
        <PageLayout
            header={
                <div className="flex justify-content-between align-items-center w-full gap-3">
                    <div>
                        <h3 className="m-0">{appStore.tr.billing}{title && ` — ${title}`}</h3>
                        <div className="text-600 mt-1">{memberBillings.total || 0}</div>
                    </div>
                    <Btn icon="pi pi-arrow-left" severity="secondary" outlined
                         onClick={() => navigate("/manager/member/list")}/>
                </div>
            }
            filters={
                <div className="grid">
                    <div className="col-6 md:col-2">
                        <Calendar className="w-full"
                                  inputStyle={{height: '40px'}}
                                  value={filter.date_from}
                                  maxDate={filter.date_to || undefined}
                                  onChange={(e) => filter.setProperty("date_from", e.value)}
                                  dateFormat="dd.mm.yy"
                                  placeholder={appStore.tr.date_from || "С даты"}/>
                    </div>
                    <div className="col-6 md:col-2">
                        <Calendar className="w-full"
                                  inputStyle={{height: '40px'}}
                                  value={filter.date_to}
                                  minDate={filter.date_from || undefined}
                                  onChange={(e) => filter.setProperty("date_to", e.value)}
                                  dateFormat="dd.mm.yy"
                                  placeholder={appStore.tr.date_to || "По дату"}/>
                    </div>
                    <div className="col-6 md:col-4">
                        <Select store={filter} prop="billing_type_id"
                                label={appStore.tr.billing_type}
                                options={clubStore.billing_types}
                                style={{height: '40px'}}
                                for_search
                        />
                    </div>
                    <div className="col-6 md:col-4">
                        <Select store={filter} prop="is_paid"
                                label={appStore.tr.payment_status}
                                options={paymentStatuses}
                                style={{height: '40px'}} for_search/>
                    </div>
                    <div className="col-6 md:col-4">
                        <Select store={filter} prop="payment_method"
                                label={appStore.tr.payment_method}
                                options={paymentMethods}
                                style={{height: '40px'}}
                                for_search/>
                    </div>
                    <div className="col-6 md:col-4">
                        <Select store={filter} prop="is_approved"
                                label={appStore.tr.approval_status}
                                options={approvalStatuses}
                                style={{height: '40px'}}
                                for_search/>
                    </div>
                    <div className="col-12 flex justify-content-end gap-2">
                        <Btn icon="pi pi-refresh" severity="secondary" style={{height: '40px', width: '44px'}}
                             outlined onClick={resetFilters}/>
                        <Btn icon="pi pi-search" severity="info" style={{height: '40px', width: '44px'}}
                             onClick={search}/>
                    </div>
                </div>
            }
            pagination={
                <NumericPagination query={memberBillings}
                                   onPageChange={(page) => loadBillings(page)}/>
            }
        >
            {memberBillings.loading ? (
                <div className="flex justify-content-center py-6">
                    <ProgressSpinner style={{width: "40px", height: "40px"}}/>
                </div>
            ) : (
                <>
                    {memberBillings.items.length === 0 && (
                        <div className="flex justify-content-center py-5">{appStore.tr.not_found}</div>
                    )}
                    {memberBillings.items.map((bill) => (
                        <BillItem key={bill.id} bill={bill}/>
                    ))}
                </>
            )}
        </PageLayout>
    );
};

export default observer(MemberBillings);